// API mijozi: token, faol biznes, avtomatik token yangilash va yagona xato formati.

const BASE = import.meta.env.VITE_API_URL ?? "/api/v1";
const STORE_KEY = "rasid.session";

/** @typedef {{ access_token: string, refresh_token: string }} Tokens */

export class ApiError extends Error {
  /** @param {number} status @param {{code?: string, message?: string, details?: any}} body */
  constructor(status, body) {
    super(body?.message || `So'rov bajarilmadi (${status})`);
    this.status = status;
    this.code = body?.code ?? "error";
    this.details = body?.details ?? {};
  }
  /** Maydon xatolari: { name: "xabar" } */
  get fields() {
    return this.code === "validation" ? this.details : {};
  }
}

const memory = { tokens: /** @type {Tokens|null} */ (null), businessId: /** @type {string|null} */ (null) };
const listeners = new Set();

function load() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) Object.assign(memory, JSON.parse(raw));
  } catch {
    /* localStorage yopiq bo'lishi mumkin */
  }
}
load();

function persist() {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(memory));
  } catch {
    /* ignore */
  }
}

export const session = {
  get tokens() {
    return memory.tokens;
  },
  get businessId() {
    return memory.businessId;
  },
  /** @param {Tokens|null} tokens */
  setTokens(tokens) {
    memory.tokens = tokens;
    persist();
    listeners.forEach((fn) => fn());
  },
  /** @param {string|null} id */
  setBusiness(id) {
    memory.businessId = id;
    persist();
    listeners.forEach((fn) => fn());
  },
  clear() {
    memory.tokens = null;
    memory.businessId = null;
    persist();
    listeners.forEach((fn) => fn());
  },
  /** @param {() => void} fn */
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

let refreshing = /** @type {Promise<boolean>|null} */ (null);

async function refresh() {
  if (!memory.tokens?.refresh_token) return false;
  refreshing ??= (async () => {
    try {
      const r = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: memory.tokens?.refresh_token }),
      });
      if (!r.ok) return false;
      session.setTokens(await r.json());
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshing = null));
    }
  })();
  return refreshing;
}

function qs(params) {
  if (!params) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

/**
 * @param {string} method
 * @param {string} path
 * @param {{ body?: any, params?: Record<string, any>, form?: boolean, signal?: AbortSignal }} [opts]
 */
export async function request(method, path, opts = {}, retried = false) {
  const headers = /** @type {Record<string,string>} */ ({ Accept: "application/json" });
  if (memory.tokens) headers.Authorization = `Bearer ${memory.tokens.access_token}`;
  if (memory.businessId) headers["X-Business-Id"] = memory.businessId;
  let body;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res;
  try {
    res = await fetch(`${BASE}${path}${qs(opts.params)}`, { method, headers, body, signal: opts.signal });
  } catch (e) {
    if (/** @type {Error} */ (e).name === "AbortError") throw e;
    throw new ApiError(0, { code: "network", message: "Server bilan aloqa yo'q — internetni tekshiring" });
  }
  if (res.status === 401 && !retried && path !== "/auth/login" && (await refresh())) {
    return request(method, path, opts, true);
  }
  if (res.status === 401) session.setTokens(null);
  if (res.status === 204) return null;
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data?.error ?? { message: res.statusText });
  return data;
}

export const api = {
  get: (path, params, signal) => request("GET", path, { params, signal }),
  post: (path, body, params) => request("POST", path, { body, params }),
  patch: (path, body) => request("PATCH", path, { body }),
  del: (path) => request("DELETE", path),
};
